/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Computer,
  ContentCopy,
  DeleteOutline,
  DesktopWindows,
  Edit,
  EventAvailable,
  ExpandMore,
  PhoneIphone,
} from '@mui/icons-material';
import {
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import { useAuth } from '../contexts/AuthContext';

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

const emptyEventForm = (date = new Date()) => ({
  id: null,
  titulo: '',
  descricao: '',
  local: '',
  dia_inteiro: true,
  inicio: format(date, 'yyyy-MM-dd'),
  fim: format(date, 'yyyy-MM-dd'),
  agendas: [],
});

const safeParseDate = (value) => {
  if (!value) return null;

  try {
    return parseISO(value);
  } catch {
    return null;
  }
};

const toInputValue = (value, allDay) => {
  const parsed = safeParseDate(value);
  if (!parsed) return '';
  return format(parsed, allDay ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm");
};

const eventOverlapsDay = (event, day) => {
  const start = safeParseDate(event.start);
  const end = safeParseDate(event.end || event.start);
  if (!start || !end) return false;

  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);
  return start <= dayEnd && end >= dayStart;
};

const sortDayEvents = (items) => {
  return [...items].sort((left, right) => {
    if (left.all_day !== right.all_day) {
      return left.all_day ? -1 : 1;
    }

    return left.start.localeCompare(right.start);
  });
};

const monthGridDays = (currentMonth) => {
  const gridStart = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
  const days = [];
  let cursor = gridStart;

  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
  }

  return days;
};

const buildEventForm = (event, fallbackDate = new Date()) => {
  if (!event) return emptyEventForm(fallbackDate);

  return {
    id: event.source_id,
    titulo: event.title || '',
    descricao: event.description || '',
    local: event.location || '',
    dia_inteiro: !!event.all_day,
    inicio: toInputValue(event.start, !!event.all_day),
    fim: toInputValue(event.end || event.start, !!event.all_day),
    agendas: Array.isArray(event.agenda_keys) ? event.agenda_keys : [],
  };
};

const buildEventPayload = (form) => {
  const endValue = form.fim || form.inicio;
  const startDate = new Date(form.dia_inteiro ? `${form.inicio}T00:00:00` : form.inicio);
  const endDate = new Date(form.dia_inteiro ? `${endValue}T23:59:59` : endValue);

  return {
    titulo: form.titulo.trim(),
    descricao: form.descricao.trim(),
    local: form.local.trim(),
    dia_inteiro: form.dia_inteiro,
    inicio_em: startDate.toISOString(),
    fim_em: endDate.toISOString(),
    agendas: form.agendas,
  };
};

const eventTimeLabel = (event) => {
  if (event.all_day) return 'Dia inteiro';
  const start = safeParseDate(event.start);
  const end = safeParseDate(event.end || event.start);
  if (!start || !end) return '-';
  return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
};

export default function AgendaModal({ open, onClose }) {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [agendas, setAgendas] = useState([]);
  const [selectedAgendaKeys, setSelectedAgendaKeys] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyEventForm(new Date()));
  const [copied, setCopied] = useState('');

  const calendarDays = monthGridDays(currentMonth);
  const selectedDayEvents = sortDayEvents(events.filter((event) => eventOverlapsDay(event, selectedDate)));

  const handleUnauthorized = useCallback(() => {
    setError('Sua sessão expirou por inatividade. Faça login novamente para continuar.');
    logout();
    onClose?.();
    navigate('/login', { replace: true, state: { sessionExpired: true } });
  }, [logout, navigate, onClose]);

  const fetchAgenda = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const gridStart = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
      const gridEnd = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
      const params = new URLSearchParams({
        start: gridStart.toISOString(),
        end: gridEnd.toISOString(),
      });

      if (selectedAgendaKeys.length > 0) {
        params.set('agendas', selectedAgendaKeys.join(','));
      }

      const res = await fetch(`${API_BASE}/agenda/events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Erro ${res.status}`);
      }

      const data = await res.json();
      const nextEvents = Array.isArray(data.events) ? data.events : [];
      const nextAgendas = Array.isArray(data.agendas) ? data.agendas : [];

      setEvents(nextEvents);
      setAgendas(nextAgendas);

      if (selectedAgendaKeys.length === 0) {
        const defaults = Array.isArray(data.selected_agendas)
          ? data.selected_agendas
          : nextAgendas.map((agenda) => agenda.key);
        setSelectedAgendaKeys(defaults);
      }

      setSelectedEvent((current) => {
        if (!current) return null;
        const refreshed = nextEvents.find((event) => event.id === current.id);
        return refreshed || null;
      });
    } catch (fetchError) {
      setError(fetchError.message || 'Erro ao carregar agenda');
    } finally {
      setLoading(false);
    }
  }, [currentMonth, selectedAgendaKeys, token, handleUnauthorized]);

  useEffect(() => {
    if (!open || !token) return;

    fetchAgenda();
  }, [open, token, fetchAgenda]);

  const handleClose = () => {
    setError('');
    setSelectedEvent(null);
    setFormOpen(false);
    onClose();
  };

  const copyToClipboard = (value, label) => {
    if (!navigator?.clipboard?.writeText) return;

    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 1800);
    });
  };

  const webcalUrl = (url) => url.replace(/^https?:\/\//, 'webcal://');

  const toggleAgendaFilter = (agendaKey) => {
    setSelectedAgendaKeys((current) => {
      if (current.includes(agendaKey)) {
        const next = current.filter((item) => item !== agendaKey);
        return next.length > 0 ? next : current;
      }

      return [...current, agendaKey];
    });
  };

  const eventsForDay = (day) => sortDayEvents(events.filter((event) => eventOverlapsDay(event, day)));

  const openCreateDialog = (date = selectedDate) => {
    const nextForm = emptyEventForm(date);
    nextForm.agendas = agendas.map((agenda) => agenda.key).slice(0, 1);
    setForm(nextForm);
    setFormOpen(true);
  };

  const openEditDialog = (event) => {
    setForm(buildEventForm(event, selectedDate));
    setFormOpen(true);
  };

  const saveEvent = async () => {
    if (!form.titulo.trim()) {
      setError('Informe um título para o evento.');
      return;
    }

    if (!form.inicio || !form.fim) {
      setError('Preencha início e fim do evento.');
      return;
    }

    if (!Array.isArray(form.agendas) || form.agendas.length === 0) {
      setError('Selecione ao menos uma agenda para incluir o evento.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = buildEventPayload(form);
      const method = form.id ? 'PATCH' : 'POST';
      const endpoint = form.id ? `${API_BASE}/agenda/events/${form.id}` : `${API_BASE}/agenda/events`;
      const res = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }
        const body = await res.json().catch(() => ({}));
        const message = body?.error || body?.errors?.join(', ') || `Erro ${res.status}`;
        throw new Error(message);
      }

      const savedEvent = await res.json();
      setSelectedDate(startOfDay(safeParseDate(savedEvent.start) || selectedDate));
      setSelectedEvent(savedEvent);
      setFormOpen(false);
      await fetchAgenda();
    } catch (saveError) {
      setError(saveError.message || 'Erro ao salvar evento');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!form.id) return;
    if (!window.confirm('Excluir este evento manual?')) return;

    setSaving(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/agenda/events/${form.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Erro ${res.status}`);
      }

      setFormOpen(false);
      setSelectedEvent(null);
      await fetchAgenda();
    } catch (deleteError) {
      setError(deleteError.message || 'Erro ao excluir evento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarMonth color="primary" />
              <Box>
                <Typography variant="h6">Agenda</Typography>
                <Typography variant="body2" color="text.secondary">
                  Calendário interno consolidado das agendas com criação de eventos manuais por checkbox.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setCurrentMonth((value) => subMonths(value, 1))}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="subtitle1" sx={{ minWidth: 180, textAlign: 'center', textTransform: 'capitalize', fontWeight: 700 }}>
                {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
              </Typography>
              <IconButton onClick={() => setCurrentMonth((value) => addMonths(value, 1))}>
                <ChevronRight />
              </IconButton>
              <Button variant="contained" startIcon={<Add />} onClick={() => openCreateDialog(selectedDate)}>
                Novo evento
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.75fr) 360px' }, gap: 2 }}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(180deg, rgba(255,247,237,0.85) 0%, rgba(255,255,255,1) 28%)',
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Agendas visíveis
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {agendas.map((agenda) => {
                    const active = selectedAgendaKeys.includes(agenda.key);
                    return (
                      <Chip
                        key={agenda.key}
                        label={agenda.label}
                        onClick={() => toggleAgendaFilter(agenda.key)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          bgcolor: active ? alpha(agenda.color, 0.18) : 'transparent',
                          color: active ? agenda.color : 'text.secondary',
                          borderColor: alpha(agenda.color, 0.45),
                          fontWeight: 600,
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 840 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {WEEKDAY_LABELS.map((label) => (
                      <Box key={label} sx={{ px: 1.5, py: 1, textAlign: 'center', bgcolor: alpha('#ef6c00', 0.06) }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                      {calendarDays.map((day) => {
                        const dayEvents = eventsForDay(day);
                        const isSelected = isSameDay(day, selectedDate);
                        const isCurrentMonth = isSameMonth(day, currentMonth);
                        const isToday = isSameDay(day, new Date());

                        return (
                          <Box
                            key={day.toISOString()}
                            onClick={() => setSelectedDate(startOfDay(day))}
                            sx={{
                              minHeight: 126,
                              p: 1,
                              borderRight: '1px solid',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              bgcolor: isSelected ? alpha('#ef6c00', 0.1) : isCurrentMonth ? 'background.paper' : alpha('#000', 0.03),
                              transition: 'background-color 120ms ease',
                              '&:hover': {
                                bgcolor: isSelected ? alpha('#ef6c00', 0.14) : alpha('#ef6c00', 0.05),
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                                  px: isToday ? 0.9 : 0,
                                  py: isToday ? 0.2 : 0,
                                  borderRadius: 99,
                                  bgcolor: isToday ? alpha('#1565c0', 0.12) : 'transparent',
                                }}
                              >
                                {format(day, 'd')}
                              </Typography>
                              {dayEvents.length > 0 && (
                                <Chip size="small" label={dayEvents.length} sx={{ height: 20, fontSize: 11 }} />
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                              {dayEvents.slice(0, 3).map((event) => (
                                <Box
                                  key={`${day.toISOString()}-${event.id}`}
                                  onClick={(clickEvent) => {
                                    clickEvent.stopPropagation();
                                    setSelectedDate(startOfDay(day));
                                    setSelectedEvent(event);
                                  }}
                                  sx={{
                                    px: 0.8,
                                    py: 0.45,
                                    borderRadius: 1.4,
                                    bgcolor: alpha(event.color || '#1565c0', 0.16),
                                    color: event.color || 'text.primary',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {event.title}
                                </Box>
                              ))}

                              {dayEvents.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{dayEvents.length - 3} adicionais
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eventos programados para o dia selecionado.
                    </Typography>
                  </Box>

                  <Button size="small" startIcon={<Add />} onClick={() => openCreateDialog(selectedDate)}>
                    Inserir
                  </Button>
                </Box>

                {selectedDayEvents.length === 0 ? (
                  <Alert severity="info">Nenhum evento neste dia.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDayEvents.map((event) => (
                      <Box
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: selectedEvent?.id === event.id ? alpha(event.color || '#1565c0', 0.5) : 'divider',
                          bgcolor: selectedEvent?.id === event.id ? alpha(event.color || '#1565c0', 0.08) : 'background.paper',
                          cursor: 'pointer',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.4 }}>
                          {event.title}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.6 }}>
                          {eventTimeLabel(event)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                          {event.agenda_labels?.map((label) => (
                            <Chip key={`${event.id}-${label}`} size="small" label={label} />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <EventAvailable color="primary" fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Detalhes do evento
                  </Typography>
                </Box>

                {!selectedEvent ? (
                  <Typography variant="body2" color="text.secondary">
                    Clique em um evento no calendário para ver os detalhes aqui.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.25 }}>
                      {selectedEvent.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {eventTimeLabel(selectedEvent)}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                      {selectedEvent.agenda_labels?.map((label) => (
                        <Chip key={`${selectedEvent.id}-${label}`} label={label} size="small" />
                      ))}
                    </Box>

                    {selectedEvent.location && (
                      <Typography variant="body2">
                        <strong>Local:</strong> {selectedEvent.location}
                      </Typography>
                    )}

                    {selectedEvent.meta?.criado_por && (
                      <Typography variant="body2">
                        <strong>Criado por:</strong> {selectedEvent.meta.criado_por}
                      </Typography>
                    )}

                    <Divider />

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedEvent.description || 'Sem descrição informada.'}
                    </Typography>

                    {selectedEvent.editable && selectedEvent.source === 'manual' && (
                      <Button startIcon={<Edit />} onClick={() => openEditDialog(selectedEvent)}>
                        Editar evento manual
                      </Button>
                    )}
                  </Box>
                )}
              </Paper>

              <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', overflow: 'hidden' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Sincronizar agenda externa
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Os feeds .ics continuam disponíveis para Apple Calendar, Google Calendar, Outlook e apps Linux.
                  </Alert>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {agendas.map((agenda) => {
                      if (!agenda.sync_url) return null;

                      return (
                        <Paper key={`sync-${agenda.key}`} variant="outlined" sx={{ p: 1.5, borderLeft: `4px solid ${agenda.color}` }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: agenda.color, mb: 0.4 }}>
                            {agenda.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.2 }}>
                            {agenda.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Button size="small" variant="outlined" startIcon={<PhoneIphone />} href={webcalUrl(agenda.sync_url)}>
                              iOS / Mac
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<CalendarMonth />} href={webcalUrl(agenda.sync_url)}>
                              Android
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<DesktopWindows />} href={agenda.sync_url} target="_blank" rel="noopener noreferrer">
                              Windows
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<Computer />} href={agenda.sync_url} target="_blank" rel="noopener noreferrer">
                              Linux
                            </Button>
                            <Tooltip title={copied === agenda.key ? 'Copiado!' : 'Copiar URL'}>
                              <IconButton size="small" onClick={() => copyToClipboard(agenda.sync_url, agenda.key)}>
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Editar evento manual' : 'Novo evento manual'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Título"
              value={form.titulo}
              onChange={(event) => setForm((current) => ({ ...current, titulo: event.target.value }))}
              fullWidth
              required
            />

            <TextField
              label="Descrição"
              value={form.descricao}
              onChange={(event) => setForm((current) => ({ ...current, descricao: event.target.value }))}
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              label="Local"
              value={form.local}
              onChange={(event) => setForm((current) => ({ ...current, local: event.target.value }))}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.dia_inteiro}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      dia_inteiro: event.target.checked,
                      inicio: current.inicio || format(selectedDate, event.target.checked ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'09:00"),
                      fim: current.fim || format(selectedDate, event.target.checked ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'18:00"),
                    }))
                  }
                />
              }
              label="Evento de dia inteiro"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Início"
                type={form.dia_inteiro ? 'date' : 'datetime-local'}
                value={form.inicio}
                onChange={(event) => setForm((current) => ({ ...current, inicio: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Fim"
                type={form.dia_inteiro ? 'date' : 'datetime-local'}
                value={form.fim}
                onChange={(event) => setForm((current) => ({ ...current, fim: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Incluir este evento nas agendas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                {agendas.map((agenda) => (
                  <FormControlLabel
                    key={`agenda-checkbox-${agenda.key}`}
                    control={
                      <Checkbox
                        checked={form.agendas.includes(agenda.key)}
                        onChange={(event) => {
                          setForm((current) => {
                            const next = event.target.checked
                              ? [...current.agendas, agenda.key]
                              : current.agendas.filter((item) => item !== agenda.key);
                            return { ...current, agendas: next };
                          });
                        }}
                      />
                    }
                    label={agenda.label}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {form.id && (
              <Button color="error" startIcon={<DeleteOutline />} onClick={deleteEvent} disabled={saving}>
                Excluir
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={saveEvent} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Add />}>
              {form.id ? 'Salvar' : 'Criar'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
